import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import {
  CMS_API_BASE,
  CUS_API_BASE,
} from '../helper/constants';
import fetchAPI from '../helper/axios';

/* ===================== TYPES ===================== */

interface ResumeFormData {
  name: string;
  email: string;
  mobile: string;
  jobTitle: string;
  description: string;
}

interface UploadResponse {
  statusCode?: number;
  url?: string;
  message?: string;
}

/* ===================== CONSTANTS ===================== */

//const SITEKEY = localStorage.getItem('site_key') ?? '';
const MAX_FILE_SIZE = 5 * 1024 * 1024;
const ALLOWED_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

/* ===================== COMPONENT ===================== */

const FooterSubCareer: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const [formData, setFormData] = useState<ResumeFormData>({
    name: '',
    email: '',
    mobile: '',
    jobTitle: '',
    description: '',
  });

  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);

  const [verified, setVerified] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const [googledata, setData] = useState<any>(null);
const [mobileError, setMobileError] = useState("");
  const formTitle= localStorage.getItem("CareerformTitle") ?? "Submit Your Resume";
  
  useEffect(() => {
      const loadData = async () => {
        try {
  
          setData(await fetchAPI('node/68'));
      
        } catch (err: any) {
          
        } finally {
         
        }
      };
  
      loadData();
    }, []);

    const  SITE_KEY = googledata?.field_google_captcher_key[0].value || '';
    //const Mediano = googledata?.field_resource_pagination_no[0].value ?? 0;
   
  /* ===================== HANDLERS ===================== */

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;

      if (name === 'mobile' && !/^\d{0,10}$/.test(value)) return;

      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        setFileError('Only PDF, DOC, or DOCX files are allowed');
        setResumeFile(null);
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        setFileError('File size must be below 5MB');
        setResumeFile(null);
        return;
      }

      setResumeFile(file);
      setFileError(null);
    },
    []
  );

  const handleCaptcha = useCallback((token: string | null) => {
    setVerified(!!token);
    setCaptchaError(false);
  }, []);

  /* ===================== SUBMIT ===================== */

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (loading) return;

    // if (!verified) {
    //   setCaptchaError(true);
    //   return;
    // }

    if (!resumeFile) {
      setFileError('Please upload your resume');
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      /* Upload Resume */
      const uploadData = new window.FormData();
      uploadData.append(
        'resume',
        new File([resumeFile], `${Date.now()}_${resumeFile.name}`, {
          type: resumeFile.type,
        })
      );

      const uploadRes = await fetch(`${CUS_API_BASE}/uploadfile.php`, {
        method: 'POST',
        body: uploadData,
      });

      const uploadJson: UploadResponse = await uploadRes.json();
      if (!uploadJson.url) {
        throw new Error(uploadJson.message || 'Resume upload failed');
      }

      /* Submit Webform */
      const submitRes = await fetch(
        `${CMS_API_BASE}/webform_rest/submit`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            webform_id: 'react_job_application',
            name: formData.name,
            job_title: formData.jobTitle,
            email: formData.email,
            mobile: `+91${formData.mobile}`,
            description: formData.description,
            resume: uploadJson.url,
          }),
        }
      );

      if (!submitRes.ok) {
        throw new Error('Form submission failed');
      }

      navigate('/thank-you');
    } catch (err: any) {
      setMessage(err.message || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };
  const isValidMobile = (mobile: string) => /^[6-9]\d{9}$/.test(mobile);
  const handleSubmitFast = async (e: React.FormEvent) => {
  e.preventDefault();
  if (loading) return;
   if (!isValidMobile(formData.mobile)) {
      setMobileError("Enter valid 10-digit mobile number");
      return;
    }
  
  if (!resumeFile) {
    setFileError('Please upload your resume');
    return;
  }
   if (!verified) {
      setCaptchaError(true);
      return;
    }
  setLoading(true);

  // 🚀 INSTANT UX
  navigate('/thank-you');

  try {
    const uploadData = new FormData();
    uploadData.append(
      'resume',
      new File([resumeFile], `${Date.now()}_${resumeFile.name}`, {
        type: resumeFile.type,
      })
    );

    const uploadRes = await fetch(`${CUS_API_BASE}/uploadfile.php`, {
      method: 'POST',
      body: uploadData,
      keepalive: true,
    });

    const uploadJson: UploadResponse = await uploadRes.json();
    if (!uploadJson.url) throw new Error('Upload failed');

    await fetch(`${CMS_API_BASE}/webform_rest/submit`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        webform_id: 'react_job_application',
        name: formData.name,
        job_title: formData.jobTitle,
        email: formData.email,
        mobile: `+91${formData.mobile}`,
        description: formData.description,
        resume: uploadJson.url,
      }),
    });
  } catch (err) {
    console.error('Background submit failed', err);
  } finally {
    setLoading(false);
  }
};

  return (
     <div className="container">
            <div className="row">

                <div className="col-xl-6 col-sm-12  pt-md-5 pt-sm-5 d-none">
                    <h3 className="mb-3">Opportunities @ gro</h3>

                    <h6 className="text-black">Cluster Head – Fleet</h6>
                    <p className="mb-lg-5 mb-md-4 mb-sm-2"> Position: 1no.  <span className="px-2"> |  </span>   Job Type: Permanent    <span className="px-2"> |  </span>   Location: Mumbai</p>


                    <h6 className="text-black"> Business Relationship Managers – Fleet</h6>
                    <p className="mb-lg-5 mb-md-4 mb-sm-2">Position: 1no.   <span className="px-2">  |  </span>   Job Type: Permanent    <span className="px-2"> | </span>    Location: Mumbai</p>


                    <h6 className="text-black">Cluster Head – Freight</h6>
                    <p className="mb-lg-5 mb-md-4 mb-sm-2">Position: 1no.   <span className="px-2">  |  </span>   Job Type: Permanent    <span className="px-2"> | </span>    Location: Mumbai</p>


                    <h6 className="text-black"> Business Relationship Manager – Freight</h6>
                    <p className="mb-lg-5 mb-md-4 mb-sm-2"> Position: 1no.   <span className="px-2">  | </span>    Job Type: Permanent   <span className="px-2">  | </span>    Location: Mumbai</p>
                </div>
                <div className="col-xl-6 col-sm-12 mx-auto pt-md-5 pt-sm-4">
                    <h3 className="mb-3">{formTitle}</h3>
                    <form onSubmit={handleSubmitFast} className="php-email-form py-2" data-aos="fade-up" data-aos-delay="200">
                        <div className="row gy-4">
                          <div className="col-md-12">
                                <input type="text" name="name" className="form-control" placeholder="Name *" value={formData.name}
                              onChange={handleChange} required />
                            </div>
                             <div className="col-md-12 ">
                                <input type="email" className="form-control" name="email" placeholder=" Email ID *" value={formData.email}
                               onChange={handleChange} required />
                            </div>
                            <div className="col-md-12">
                                <input type="text" name="jobTitle" className="form-control" placeholder="Job Title *" value={formData.jobTitle}
                                    onChange={handleChange} required />
                            </div>
                            <div className="col-md-12 d-flex">
                                <span className="input-group-text nocode">+91</span>
                                <input type="number" className="form-control form-control-border" name="mobile" placeholder="Mobile No  *" value={formData.mobile}
              onChange={handleChange} required />
                            </div>
                               {mobileError && <small className="text-danger mt-0 col-sm-12">{mobileError}</small>}

                            <div className="col-md-12">
                                <textarea className="form-control" name="description"  placeholder="Describe Yourself" value={formData.description}
                                  onChange={handleChange} required />
                            </div>
                             <div className="col-md-12">
                                <input
                                            type="file"
                                            ref={fileInputRef}
                                            className="form-control mb-2"
                                            accept=".pdf,.doc,.docx"
                                            onChange={handleFileChange}
                                          />
                              </div>
            {fileError && <p className="text-danger mt-0">{fileError}</p>}
  <div className="col-md-12">
            {SITE_KEY && (
              <ReCAPTCHA sitekey={SITE_KEY} onChange={handleCaptcha} />
            )}

            {captchaError && (
              <p className="text-danger mt-1">
                Please complete reCAPTCHA
              </p>
            )}
            </div>
                            <div className="col-md-12 ">
                                 <button
              type="submit"
              className="btn btn-success mt-3"
              disabled={loading}
            >
              {loading ? 'Submitting...' : 'Submit'}
            </button>

            {message && <p className="mt-2">{message}</p>}
                            </div>

                        </div>
                    </form>
                </div>
            </div>
        </div>
  );
};

export default FooterSubCareer;
